import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import Modal from '../../Modal';
import HotelFilterBar from './HotelFilterBar';
import HotelList from './HotelList';
import {
  ListPageSize,
  InfoPageSize,
  FiltersInitialValue,
  TravelTypes,
  PageLinks,
} from '../../../constants';
import useHotelSortingValues from '../../../hooks/useHotelSortingValues';
import { hotelSearch, setIsUserInteractedWithHotelList } from '../../../store/slices/hotel';
import SortingFilter from '../../Filters/SortingFilter';
import HotelsModalContent from './HotelsModalContent';
import HotelItinerary from './HotelItinerary';
import PassengersWarningMessage from './PassengersWarningMessage';

dayjs.extend(dayjsPluginUTC);

const HotelInformation = ({
  departureCityName,
  defaultDepartureCityId,
  defaultCruiseDepartDate,
  tripId,
  cruiseItineraryId,
  searchDestination,
  checkInDate,
  checkOutDate,
  doShowAsModalOnly,
  onModalClose,
  doShowChatGpt,
  travelOption,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const departureCityId = defaultDepartureCityId
    || (travelOption && travelOption.itineraryDepartCityId);
  const travelOptionDepartDate = defaultCruiseDepartDate
    || (travelOption && travelOption.itineraryRows
      && travelOption.itineraryRows.find((i) => i.itineraryId === cruiseItineraryId)
        .itineraryDepartDate)
    || (travelOption && travelOption.startDateTime);
  const [isModalOpen, setIsModalOpen] = useState(doShowAsModalOnly);
  const hotelSearchState = useSelector((store) => store.hotel.hotelSearches[tripId]);
  const specificHotelNotFoundMessage = useSelector((store) => {
    if (store.hotel.hotelSearches && hotelSearchState && hotelSearchState.queryString
       && store.hotel.hotelSearchResponses
       && store.hotel.hotelSearchResponses[hotelSearchState.queryString]) {
      // eslint-disable-next-line max-len
      return store.hotel.hotelSearchResponses[hotelSearchState.queryString].specificHotelNotFoundMessage;
    }
    return undefined;
  });
  const { travelAgency } = useSelector((store) => store.user);
  const sortingValues = useHotelSortingValues(tripId);
  const trip = useSelector((store) => store.trip.selectedTrips[tripId]);
  const defaultSearchLocationName = departureCityName || (searchDestination || {}).name;
  const { pageName } = router.query;
  const changeHotelPage = pageName === PageLinks.ChangeHotel.pageName;
  const selectedHotelLabel = changeHotelPage ? 'Booked hotel' : 'Selected hotel';

  const hotelCheckInDate = useMemo(() => {
    if (hotelSearchState && hotelSearchState.searchObj) {
      const { searchObj, defaultSearchObj } = hotelSearchState;
      if (searchObj.checkInDate !== defaultSearchObj.checkInDate) {
        return dayjs(hotelSearchState.searchObj.checkInDate);
      }
    }
    if (travelOption && travelOption.mode === TravelTypes.flight) {
      return dayjs(travelOption.flightItinerary.flightSegments[0].arrivalDateTime);
    }
    if (travelOption && travelOption.mode === TravelTypes.carrental) {
      return dayjs(travelOption.carItinerary.startDateTime);
    }
    return checkInDate
      ? dayjs(checkInDate)
      : dayjs(travelOptionDepartDate).subtract(1, 'days');
  }, [hotelSearchState, travelOption, checkInDate, travelOptionDepartDate]);

  const hotelCheckOutDate = useMemo(() => {
    if (hotelSearchState && hotelSearchState.searchObj) {
      const { searchObj, defaultSearchObj } = hotelSearchState;
      if (searchObj.checkOutDate !== defaultSearchObj.checkOutDate) {
        return dayjs(hotelSearchState.searchObj.checkOutDate);
      }
    }
    if (travelOption && travelOption.mode === TravelTypes.flight) {
      if (travelOption.flightItinerary.flightSegments
        && travelOption.flightItinerary.flightSegments.length > 1
        && travelOption.flightItinerary.flightSegments[1]) {
        return dayjs(travelOption.flightItinerary.flightSegments[1].departureDateTime);
      } return dayjs(travelOption.flightItinerary.flightSegments[0].arrivalDateTime).add(1, 'day');
    }
    if (travelOption && travelOption.mode === TravelTypes.carrental) {
      return dayjs(travelOption.carItinerary.endDateTime);
    }
    return checkOutDate
      ? dayjs(checkOutDate)
      : dayjs(travelOptionDepartDate);
  }, [hotelSearchState, travelOption, checkOutDate, travelOptionDepartDate]);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    track('hotelInformation.handleOpenModal');
  }, [track]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    if (onModalClose) {
      onModalClose(null);
    }
    track('hotelInformation.handleCloseModal');
  }, [onModalClose, track]);

  const handleSortChange = useCallback((searchObj) => {
    dispatch(setIsUserInteractedWithHotelList(true));
    dispatch(hotelSearch({ tripId, searchObj }));
  }, [dispatch, tripId]);

  useEffect(() => {
    const commonObj = {
      checkInDate: hotelCheckInDate.format('MM/DD/YYYY'),
      checkOutDate: hotelCheckOutDate.format('MM/DD/YYYY'),
      pageSize: isModalOpen ? ListPageSize : InfoPageSize,
      departureCityId,
      latitude: (searchDestination || {}).latitude,
      longitude: (searchDestination || {}).longitude,
    };
    if (searchDestination && searchDestination.iata) {
      commonObj.airportGeoId = searchDestination.id;
      commonObj.airportGeoFeatureCode = searchDestination.featureClass;
    } else if (searchDestination && searchDestination.googlePlaceId) {
      commonObj.placeId = searchDestination.googlePlaceId;
    } else if (searchDestination && searchDestination.hotelId) {
      commonObj.preferredHotelId = searchDestination.hotelId;
    }
    commonObj.city = searchDestination?.city;
    if (!hotelSearchState || !hotelSearchState.searchObj) {
      commonObj.doShowOnlyAgentPreferredHotels = travelAgency.defaultShowAgencyPreferredHotels;
    }
    if (!hotelSearchState || !hotelSearchState.searchObj) {
      commonObj.doShowAvailableHotelsOnly = !travelAgency.filterSettings
        || travelAgency.filterSettings.doShowAvailableHotelsOnly;
    }
    dispatch(hotelSearch({
      tripId,
      searchObj: commonObj,
      defaultSearchObj: hotelSearchState && hotelSearchState.defaultSearchObj
        ? hotelSearchState.defaultSearchObj : commonObj,
    }));
    track('hotelInformation.startHotelSearch', {
      searchObj: JSON.stringify(commonObj),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const { checkIn, checkOut } = {
    checkIn: hotelCheckInDate.format('MMM DD'),
    checkOut: hotelCheckOutDate.format('MMM DD'),
  };
  return (
    <div className="mx-10 my-5">
      {!doShowAsModalOnly
        && (
          <>
            {!changeHotelPage
            && trip
            && trip.hotelItineraries
            && (Object.keys(trip.hotelItineraries).length > 0)
            && (
            <>
              <div className="mb-5 font-bold">
                {selectedHotelLabel}
              </div>
              {(() => {
                const hotelItinerariesArray = Array.isArray(trip.hotelItineraries)
                  ? trip.hotelItineraries
                  : Object.values(trip.hotelItineraries);

                // Takes only the first itinerary Per hotel in order to avoid duplicate
                // hotel blocks when more than one room is selected in the same hotel
                const firstItinerariesPerHotel = hotelItinerariesArray.reduce((acc, current) => {
                  if (!acc.some((hotel) => hotel.hotelKey === current.hotelKey)) {
                    acc.push(current);
                  }
                  return acc;
                }, []);

                return firstItinerariesPerHotel.map((itinerary) => (
                  <HotelItinerary
                    key={itinerary.id}
                    itinerary={itinerary}
                    tripId={tripId}
                    isInsideModal={false}
                    isHotelSelected={!changeHotelPage}
                    doShowChatGpt={doShowChatGpt}
                    travelOption={travelOption}
                  />
                ));
              })()}
            </>
            )}
            <div className="font-bold">
              Filter by
            </div>
            <HotelFilterBar defaultSearchLocationName={defaultSearchLocationName} tripId={tripId} />
            <PassengersWarningMessage />
            {specificHotelNotFoundMessage && (
            <div className="mt-8 bg-orange-200 px-4 py-2 rounded-lg border border-orange-400 min-w-full mb-4 text-black text-center">
              <p>{specificHotelNotFoundMessage}</p>
            </div>
            )}
            <div className="mt-10 mb-5 font-bold flex justify-between items-center">
              Best hotels (
              {checkIn}
              {' '}
              -
              {' '}
              {checkOut}
              )
              {hotelSearchState && (
                <SortingFilter
                  sortingValues={sortingValues}
                  initialValue={FiltersInitialValue.hotel.sortBy}
                  value={hotelSearchState.searchObj.sortBy}
                  direction={hotelSearchState.searchObj.sortDirection}
                  onSortChange={handleSortChange}
                  variant="transparentOutline"
                />
              )}
            </div>
            <HotelList
              onViewMoreClick={handleOpenModal}
              tripId={tripId}
              onModalClose={handleCloseModal}
              doShowChatGpt={doShowChatGpt}
              doExcludeSelectedHotel={!changeHotelPage}
              travelOption={travelOption}
            />
          </>
        )}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
      >
        <div className="px-8 grid grid-flow-col auto-cols-max gap-5 font-bold border-b-2">
          <a
            role="button"
            tabIndex="-1"
            className="text-theme border-b-2 border-theme py-4 w-24 text-center"
          >
            Hotel
          </a>
        </div>
        <HotelsModalContent
          defaultSearchLocationName={defaultSearchLocationName}
          tripId={tripId}
          hotelCheckInDate={checkIn}
          hotelCheckOutDate={checkOut}
          handleOpenModal={handleOpenModal}
          handleCloseModal={handleCloseModal}
          onModalClose={handleCloseModal}
          onSortChange={handleSortChange}
          selectedHotelLabel={selectedHotelLabel}
          travelOption={travelOption}
        />
      </Modal>
    </div>
  );
};

HotelInformation.propTypes = {
  defaultDepartureCityId: PropTypes.number,
  defaultCruiseDepartDate: PropTypes.string,
  cruiseItineraryId: PropTypes.string,
  departureCityName: PropTypes.string,
  tripId: PropTypes.string.isRequired,
  searchDestination: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    iata: PropTypes.string,
    id: PropTypes.number,
    featureClass: PropTypes.string,
    googlePlaceId: PropTypes.string,
    hotelId: PropTypes.string,
    city: PropTypes.string,
  }),
  doShowAsModalOnly: PropTypes.bool,
  checkInDate: PropTypes.string,
  checkOutDate: PropTypes.string,
  onModalClose: PropTypes.func,
  doShowChatGpt: PropTypes.bool,
  travelOption: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.number,
    itineraryDepartCity: PropTypes.string,
    itineraryDepartCityId: PropTypes.number,
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    itinerary: PropTypes.shape(),
    itineraryRows: PropTypes.arrayOf(PropTypes.shape()),
    flightItinerary: PropTypes.shape({
      flightSegments: PropTypes.arrayOf(PropTypes.shape()),
    }),
    hotelItinerary: PropTypes.shape({
      id: PropTypes.number,
    }),
    carItinerary: PropTypes.shape({
      id: PropTypes.number,
      destinationGeoname: PropTypes.shape(),
      startDateTime: PropTypes.string,
      endDateTime: PropTypes.string,
    }),
  }).isRequired,
};

HotelInformation.defaultProps = {
  defaultDepartureCityId: undefined,
  defaultCruiseDepartDate: undefined,
  departureCityName: null,
  doShowAsModalOnly: false,
  searchDestination: undefined,
  checkInDate: undefined,
  checkOutDate: undefined,
  onModalClose: undefined,
  doShowChatGpt: true,
  cruiseItineraryId: undefined,
};

export default React.memo(HotelInformation);
